<template>
  <v-card class="ma-0 pa-0 ml-2" color="white" height="100%" :width="width">
    <v-container fluid fill-width class="pa-0 ma-0">
      <!-- Variable icon/title/reset button -->
      <v-row
        class="subheading primary--text text--darken-4 pa-0 ma-0 text-truncate pt-1"
      >
        <v-col cols="12" class="ma-0 pa-0">
          <v-card flat>
            <v-card-title class="primary--text pl-3 py-2">
              <img
                :src="'/images/' + variable.category + '-icon-128.png'"
                :title="variable.category"
                style="height: 3em;"
                @error="missingDomainFallback"
              />

              <div class="text-h6 ml-2 text-wrap pl-2" style="max-width: 65%;">
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      {{ getVariableAbbreviation(variable) }}
                    </span>
                  </template>
                  <span>{{ getVariableLabel(variable) }}</span>
                </v-tooltip>
              </div>

              <v-spacer />

              <v-btn
                class="primary text--white ma-0 px-2 py-0"
                @click="resetClicked"
                >Reset</v-btn
              >
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <!-- End of Variable icon/title/reset button -->
    </v-container>

    <v-container fluid class="pa-0 ma-0">
      <v-row class="pa-0 ma-0">
        <v-col cols="12" class="pa-0 ma-0">
          <div v-if="variable.value_type === 'date'" class="pl-3">
            Date types not supported.
          </div>
          <ParallelCoordinates
            v-if="
              variable.is_longitudinal && variable.data_category == 'Continuous'
            "
            ref="parallelcoords"
            :dimension-name="dimension"
            :variable="variable"
            :first-visit-label="getFirstVisitLabel()"
            :last-visit-label="getLastVisitLabel()"
            :show-filter-help="showFilterHelp"
            @savePredefinedRanges="savePredefinedRanges"
            @comparePredefinedRanges="comparePredefinedRanges"
            @userChangedVariable="userChangedVariable"
            @userResetVariable="userChangedVariable"
          />
          <MultiBarChart
            v-else-if="
              variable.is_longitudinal &&
                variable.data_category == 'Categorical'
            "
            :dimension-name="dimension"
            :variable="variable"
            :show-filter-help="showFilterHelp"
            :first-visit-label="getFirstVisitLabel()"
            :last-visit-label="getLastVisitLabel()"
            @userChangedVariable="userChangedVariable"
          />
          <HistogramChart
            v-else-if="
              !variable.is_longitudinal &&
                variable.data_category == 'Continuous'
            "
            :id="variable.id"
            :dimension-name="dimension"
            :variable="variable"
            :show-best-and-worst="
              variable.type == 'observation' &&
                (!variable.is_longitudinal ||
                  (variable.label == 'First Visit' ||
                    variable.label == 'Last Visit'))
            "
            :show-filter-help="showFilterHelp"
            @savePredefinedRanges="savePredefinedRanges"
            @comparePredefinedRanges="comparePredefinedRanges"
            @userChangedVariable="userChangedVariable"
          />
          <BarChart
            v-else
            :id="variable.id"
            :dimension-name="dimension"
            :variable="variable"
            :show-filter-help="showFilterHelp"
            @userChangedVariable="userChangedVariable"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import ParallelCoordinates from '@/components/CohortManager/OutcomesAnalysis/ParallelCoordinates.vue';
import MultiBarChart from '@/components/CohortManager/OutcomesAnalysis/MultiBarChart.vue';
import HistogramChart from '@/components/CohortManager/HistogramChart/HistogramChart.vue';
import BarChart from '@/components/CohortManager/BarChart/BarChart.vue';
import { mapActions, mapState } from 'vuex';
import { state, actions } from '@/store/modules/cohortManager/types';
import { missingDomainFallback } from '@/utils/helpers';

export default {
  components: {
    ParallelCoordinates,
    MultiBarChart,
    HistogramChart,
    BarChart,
  },
  props: {
    showFilterHelp: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      dimension: null,
      resetCount: 0,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('cohortManager', {
      collection: state.COLLECTION,
      dimensions: state.DIMENSIONS,
      inputVariables: state.INPUT_VARIABLES,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    variable() {
      if (this.collection.is_longitudinal) {
        let v = null;
        this.collection.observation_variables_list.forEach(ov => {
          if (ov.ontology.id == this.inputVariables[0].parentID) {
            v = ov.ontology;
          }
        });
        return v;
      } else {
        return this.inputVariables[0];
      }
    },
    width() {
      if (this.variable.is_longitudinal) {
        if (this.variable.data_category == 'Categorical') {
          return this.showFilterHelp ? '65%' : '55%';
        } else {
          return '100%';
        }
      } else {
        return '35%';
      }
    },
  },
  watch: {
    inputVariables() {
      this.clearAllFilters();
      if (this.variable.children) {
        this.variable.children.forEach(childVariable => {
          this.addDimensionHelper(childVariable);
        });
      } else {
        this.addDimensionHelper(this.variable);
      }
    },
  },
  created() {
    if (this.variable.children) {
      this.variable.children.forEach(childVariable => {
        this.addDimensionHelper(childVariable);
      });
    } else {
      this.addDimensionHelper(this.variable);
    }
  },
  methods: {
    ...mapActions('cohortManager', {
      addDimension: actions.ADD_DIMENSION,
      clearAllFilters: actions.CLEAR_ALL_FILTERS,
    }),
    resetClicked() {
      this.clearAllFilters();
      if (
        this.variable.is_longitudinal &&
        this.variable.data_category == 'Continuous'
      ) {
        this.$refs.parallelcoords.resetAllClusterSelection();
      }
      this.$emit('userResetOutputVariable', this.dimension);
    },
    userChangedOutputVariable() {
      this.$emit('userChangedOutputVariable', this.dimension);
    },
    addDimensionHelper(variable) {
      var measure = null;
      var dimensionName = null;
      var dimensionId = null;

      if (variable.is_longitudinal) {
        measure = variable.id.split('-')[0];
        dimensionName = `${variable.parentLabel} - ${variable.label}`;
        dimensionId = variable.parentID;
        this.dimension = dimensionId;
      } else {
        measure = 'value';
        dimensionName = variable.label;
        dimensionId = variable.id;
        this.dimension = dimensionName;
      }

      const payload = {
        dimensionName,
        accessor: d => {
          const dimName = dimensionId;
          return d[dimName][measure];
        },
      };
      this.addDimension(payload);
    },
    getVariableAbbreviation(v) {
      var abbrev = this.useLongScaleNames ? v.label : v.abbreviation;
      // var visits = `${this.getFirstVisitLabel()} - ${this.getLastVisitLabel()}`;
      if (v.parentLabel && v.parentLabel != abbrev) {
        return `${v.parentLabel} - ${abbrev}`;
      }
      // return `${abbrev}: ${visits}`;
      return abbrev;
    },
    getVariableLabel(v) {
      var lbl = this.useLongScaleNames ? v.description : v.label;
      // var visits = `${this.getFirstVisitLabel()} - ${this.getLastVisitLabel()}`;
      if (v.parentLabel && v.parentLabel != lbl) {
        return `${v.parentLabel} - ${lbl}`;
      }
      // return `${lbl}: ${visits}`;
      return lbl;
    },
    getVisitLabel(isFirst) {
      var which = isFirst ? 'first' : 'last';
      var result = '?';
      this.collection.observation_variables_list.forEach(ov => {
        if (ov.ontology.id == this.variable.id) {
          if (ov[which + '_visit_event']) {
            result = String(ov[which + '_visit_event']);
          } else {
            result = String(ov[which + '_visit_num']);
          }
        }
      });
      return result;
    },
    getFirstVisitLabel() {
      return this.getVisitLabel(true);
    },
    getLastVisitLabel() {
      return this.getVisitLabel(false);
    },
    getSelectedMeasuresKey(v) {
      var key = '';
      if (!('selected_measures' in v)) {
        return 'all';
      }

      ['firstVisit', 'lastVisit', 'change', 'roc'].forEach(m => {
        if (m in v['selected_measures'] && v['selected_measures'][m]) {
          key += '1';
        } else {
          key += '0';
        }
      });
      return key;
    },
    userChangedVariable() {
      this.$emit('userChangedOutputVariable');
    },
    comparePredefinedRanges(ranges) {
      this.$emit('comparePredefinedRanges', ranges);
    },
    savePredefinedRanges(ranges) {
      this.$emit('savePredefinedRanges', ranges);
    },
  },
};
</script>

<style lang="scss" scoped></style>
